import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShoppingCart, Mail, Phone, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function AbandonedCarts() {
  const [data, setData] = useState(null);
  const [deleting, setDeleting] = useState("");

  const load = () => api.get("/sales/abandoned-carts").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const remove = async (cart) => {
    if (!window.confirm(`Delete unfinished checkout ${cart.reference}? Nothing was charged and this cannot be undone.`)) return;
    setDeleting(cart.id);
    try {
      await api.delete(`/sales/abandoned-carts/${cart.id}`);
      toast.success(`${cart.reference} deleted`);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Could not delete this basket")); }
    finally { setDeleting(""); }
  };

  if (!data) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-ei-navy" /></div>;
  }

  return (
    <div data-testid="abandoned-carts-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ei-navy flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-ei-golddark" /> Unfinished checkouts
          </h1>
          <p className="mt-1 text-sm text-ei-graphite/70">Customers who started a card checkout and never paid. Nothing has been charged.</p>
        </div>
        <div className="text-sm text-ei-graphite/70">
          <span className="font-semibold text-ei-navy tnum" data-testid="abandoned-count">{data.count}</span> basket{data.count !== 1 ? "s" : ""} ·
          <span className="font-semibold text-ei-navy tnum ml-1" data-testid="abandoned-value">{money(data.value_inc_gst)}</span> inc GST
        </div>
      </div>

      {data.count === 0 ? (
        <p className="mt-10 text-center text-sm text-ei-graphite/60" data-testid="abandoned-empty">No unfinished checkouts.</p>
      ) : (
        <ul className="mt-6 space-y-4">
          {data.carts.map((c) => (
            <li key={c.id} className="bg-white border border-ei-mist rounded-md p-4 sm:p-5" data-testid={`abandoned-cart-${c.reference}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-ei-navy">{c.company || c.name || c.email}</p>
                  <p className="mt-1 text-sm text-ei-graphite/70 break-words">{c.name && c.company ? `${c.name} · ` : ""}{c.email}</p>
                  <p className="mt-1 text-xs text-ei-graphite/60 tnum">{c.reference} · {when(c.created_at)}{c.environment === "preview" ? " · preview" : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-ei-navy tnum">{money(c.total_charged_inc_gst)}</p>
                  <p className="text-xs text-ei-graphite/60">{c.fulfilment === "pickup" ? "Collection" : "Delivery"}{c.dispatch_warehouse ? ` · ${c.dispatch_warehouse}` : ""}</p>
                </div>
              </div>

              <ul className="mt-3 border-t border-ei-mist pt-3 space-y-1">
                {c.lines.map((l, i) => (
                  <li key={l.sku || `${l.name}-${i}`} className="text-sm text-ei-graphite/80 flex justify-between gap-3">
                    <span className="min-w-0">{l.quantity} × {l.name}</span>
                    <span className="tnum whitespace-nowrap">{money((Number(l.unit_price) || 0) * (Number(l.quantity) || 0))}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex flex-wrap gap-3">
                <a href={`mailto:${c.email}?subject=Your%20EI%20Battery%20basket%20${c.reference}`} className="inline-flex items-center gap-2 text-sm font-semibold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy" data-testid={`abandoned-email-${c.reference}`}>
                  <Mail className="w-4 h-4" /> Email customer
                </a>
                {c.phone && (
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-sm font-semibold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy" data-testid={`abandoned-call-${c.reference}`}>
                    <Phone className="w-4 h-4" /> {c.phone}
                  </a>
                )}
                <button type="button" onClick={() => remove(c)} disabled={deleting === c.id}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-700 border border-red-200 px-4 py-2.5 rounded-sm hover:border-red-500 hover:bg-red-50 disabled:opacity-50"
                  data-testid={`abandoned-delete-${c.reference}`}>
                  {deleting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
